// App state (Svelte 5 runes). Views read data and settings from here; state changes refresh the UI automatically.
import * as E from "../core/engine";
import type { AggregateResult, Granularity, Model } from "../core/types";
import type { ApiData, ApiMarket } from "../data/types";
import type { InitResult } from "../worker/protocol";
import { store } from "./store";

export type PeriodKey = "1T" | "1M" | "3M" | "6M" | "YTD" | "1R" | "3L" | "5L" | "MAX" | "CUSTOM";
export interface Period { k: PeriodKey; label: string; days?: number }
export const PERIODS: Period[] = [
  { k: "1T", label: "1T", days: 7 }, { k: "1M", label: "1M", days: 30 }, { k: "3M", label: "3M", days: 91 }, { k: "6M", label: "6M", days: 182 },
  { k: "YTD", label: "YTD" }, { k: "1R", label: "1R", days: 365 }, { k: "3L", label: "3L", days: 1095 }, { k: "5L", label: "5L", days: 1826 },
  { k: "MAX", label: "MAX" }, { k: "CUSTOM", label: "Zakres" },
];
export const PERIOD_NAMES: Record<PeriodKey, string> = {
  "1T": "tydzień", "1M": "miesiąc", "3M": "3 miesiące", "6M": "6 miesięcy", YTD: "od początku roku", "1R": "rok",
  "3L": "3 lata", "5L": "5 lat", MAX: "cała historia", CUSTOM: "wybrany zakres",
};

/** View settings remembered in the browser (key portfel.ui). */
export interface UiState {
  showClosed?: boolean;
  groupBonds?: boolean;
  instrMode?: "pln" | "ccy";
  purchaseKey?: string;
  retGran?: Granularity;
  allocDim?: string;
  rebExtra?: number;
  fxMode?: "pct" | "abs";
  taxYear?: number;
  txf?: { q?: string; type?: string; year?: string };
  contribBy?: "instrument" | "group";
  alt?: { spread: number };
  cal?: { infl?: number; gran?: "year" | "month" };
  proj?: { monthly?: number; years?: number; inflation?: number; growContrib?: boolean; pes?: number; base?: number; opt?: number; expenses?: number; swr?: number; view?: "real" | "nominal" };
  ike?: { pit?: number };
}

export type Screen = "loading" | "welcome" | "app" | "error";

const VIEW_NAMES = new Set([
  "dashboard", "holdings", "allocation", "performance", "returns", "attribution", "purchases", "bonds", "ike", "projection", "alternative",
  "taxes", "costs", "cashflows", "transactions", "import", "settings",
]);
export const routeFromHash = (hash: string): string => {
  const v = (hash.replace(/^#\/?/, "") || "dashboard").split("?")[0];
  return VIEW_NAMES.has(v) ? v : "dashboard";
};

class AppState {
  // folder data and quotes (large structures: no deep reactivity, change = replace the object)
  data = $state.raw<ApiData | null>(null);
  market = $state.raw<ApiMarket | null>(null);
  marketError = $state<string | null>(null);
  model = $state.raw<Model | null>(null);
  /** opened folder: kind, name, whether writable */
  fs = $state.raw<InitResult | null>(null);

  // filters and toggles
  accounts = $state.raw<Set<string>>(new Set());
  period = $state<PeriodKey>(store.get("period", "1R"));
  custom = $state<{ start?: string; end?: string } | null>(store.get("custom", null));
  tax = $state<boolean | null>(null);
  privacy = $state<boolean>(store.get("privacy", false));
  ui = $state<UiState>(store.get("ui", {}));
  /** changes when the theme is switched — charts re-read colours from CSS */
  theme = $state(0);

  // shell
  route = $state("dashboard");
  screen = $state<Screen>("loading");
  loadingMsg = $state("Wczytywanie danych…");
  bootError = $state("");
  welcomeError = $state("");
  /** text in the sidebar footer; null = data summary */
  status = $state<string | null>(null);
  busy = $state(false);
  drawerKey = $state<string | null>(null);
  toast = $state<string | null>(null);

  /** selected accounts (in selection order) */
  accList = $derived([...this.accounts]);
  allAccounts = $derived(!!this.model && this.accounts.size === this.model.accounts.length);
  /** aggregate of the selected accounts: values, contributions, tax, day by day */
  agg = $derived.by((): AggregateResult | null => (this.model ? E.aggregate(this.model, this.accList, !!this.tax) : null));
  range = $derived(this.model ? periodRange(this.model, this.period, this.custom) : { start: "", end: "" });
}

export const app = new AppState();

export function periodRange(m: Pick<Model, "firstDay" | "today">, k: PeriodKey, custom: { start?: string; end?: string } | null = null): { start: string; end: string } {
  const minStart = E.addDays(m.firstDay, 1);
  const end = m.today;
  if (k === "MAX") return { start: minStart, end };
  if (k === "YTD") {
    const s = end.slice(0, 4) + "-01-01";
    return { start: s < minStart ? minStart : s, end };
  }
  if (k === "CUSTOM") {
    const c = custom || {};
    let s = c.start || E.addDays(end, -364), e = c.end || end;
    if (s < minStart) s = minStart;
    if (e > end) e = end;
    if (e < s) e = s;
    return { start: s, end: e };
  }
  const p = PERIODS.find((x) => x.k === k)!;
  const s = E.addDays(end, -((p.days as number) - 1));
  return { start: s < minStart ? minStart : s, end };
}

/** Range of period `k` for the current model (period table on the dashboard). */
export const rangeOf = (k: PeriodKey): { start: string; end: string } => periodRange(need(app.model), k, app.custom);

/** Views render only once the model is loaded — asserting shorthands. */
export function need<T>(v: T | null | undefined): T {
  if (v === null || v === undefined) throw new Error("Data is not loaded yet");
  return v;
}

// persist view settings on every change
if (typeof window !== "undefined") {
  $effect.root(() => {
    $effect(() => store.set("ui", $state.snapshot(app.ui)));
  });
}
